// Unit (waffle) chart: one square per respondent.
//
// Figma node 7102:10799, "One square is one respondent in the current
// filter." Its value is that the reader counts people rather than reading a
// proportion, which makes a small sample feel like the handful of people it
// actually is instead of implying survey-grade precision.
//
// That only holds while the squares are countable. Past a couple of hundred
// the grid stops being countable and starts being a texture, at which point
// a labelled bar is the honest form, so this falls back automatically and
// says why.
import React, { useMemo } from "react";
import styled from "styled-components";
import { Tooltip } from "antd";
import { FONT, FIG_MUTED, TRACK } from "./tokens";
import { BarRow, BarLabel, BarTrack, BarValue, LegendRow, LegendItem } from "./primitives";

// Above this many units the grid is no longer countable.
export const UNIT_CHART_MAX = 150;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => p.$gap}px;
  margin: 4px 0 2px;
`;

const Cell = styled.span`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: ${(p) => Math.max(2, Math.round(p.$size / 4.5))}px;
  background: ${(p) => p.$color};
  flex: none;
`;

const Fill = styled.span`
  display: block;
  height: 100%;
  border-radius: 999px;
  background: ${(p) => p.$color};
`;

const Note = styled.p`
  font-family: ${FONT};
  font-size: 12px;
  line-height: 18px;
  color: ${FIG_MUTED};
  margin: 12px 0 0;
`;

/**
 * @param {Array<{label,n,color}>} groups   segments, drawn in order
 * @param {number} size    square edge in px
 * @param {number} gap     gap in px
 * @param {function} onSelect  optional; called with the group
 */
export default function UnitChart({ groups = [], size = 14, gap = 6, onSelect }) {
  const total = useMemo(
    () => groups.reduce((a, g) => a + (g.n || 0), 0),
    [groups],
  );

  const cells = useMemo(() => {
    if (!total || total > UNIT_CHART_MAX) return [];
    return groups.flatMap((g) =>
      Array.from({ length: g.n || 0 }, (_, i) => ({ ...g, key: `${g.label}-${i}` })),
    );
  }, [groups, total]);

  if (!total) return <Note>No respondents in the current filter.</Note>;

  // Too many to count: a labelled bar per group says the same thing without
  // pretending the reader can tally 400 squares.
  if (total > UNIT_CHART_MAX) {
    const max = Math.max(...groups.map((g) => g.n || 0), 1);
    return (
      <>
        {groups.map((g) => (
          <BarRow
            key={g.label}
            $clickable={!!onSelect}
            onClick={onSelect ? () => onSelect(g) : undefined}
          >
            <BarLabel title={g.label}>{g.label}</BarLabel>
            <BarTrack>
              <Fill $color={g.color} style={{ width: `${(100 * (g.n || 0)) / max}%` }} />
            </BarTrack>
            <BarValue>
              <b>{g.n}</b>
              <small>{Math.round((100 * (g.n || 0)) / total)}%</small>
            </BarValue>
          </BarRow>
        ))}
        <Note>
          {total} respondents is too many to count as squares, so each group is drawn as a bar.
        </Note>
      </>
    );
  }

  return (
    <>
      <Grid $gap={gap}>
        {cells.map((c) => (
          <Tooltip key={c.key} title={`${c.label} · ${c.n} of ${total}`}>
            <Cell $size={size} $gap={gap} $color={c.color} />
          </Tooltip>
        ))}
      </Grid>
      <LegendRow>
        {groups.map((g) => (
          <LegendItem key={g.label} $color={g.color}>
            {g.label} <b>{g.n}</b>
          </LegendItem>
        ))}
      </LegendRow>
    </>
  );
}
